import React, { useState, useEffect } from "react";
import { FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import ReactCountryFlag from "react-country-flag";
import Header from "../../components/Header";
import "./Profile.css";
import PreviewProfileModal from '../../components/PreviewProfileModal';
import OnboardingChatbot from "../../components/OnboardingChatbot";
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, updateDoc, collection, getDocs, query, where, addDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { uploadToCloudinary } from '../../utils/cloudinary';
import CreatableSelect from 'react-select/creatable';
import ISO6391 from 'iso-639-1';
import { getNames as getCountryNames, getCode as getCountryCode } from 'country-list';
import { City } from 'country-state-city';
import ProfileDinnerCard from '../../components/ProfileDinnerCard';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import HostCardProfile from '../../components/HostCardProfile';
import { countryToAlpha2 } from 'country-to-iso';


const defaultValues = {
    languages: "Let others know which languages you feel comfortable speaking during the dinner.",
    interests: "Share a few of your hobbies or passions – it's a great way to connect with your host or guests."
};

// interests emoji list
const INTERESTS = [
    { emoji: "🎶", label: "Music" },
    { emoji: "🍳", label: "Cooking" },
    { emoji: "🎨", label: "Art & Design" },
    { emoji: "🎥", label: "Movies & Series" },
    { emoji: "📚", label: "Books & Reading" },
    { emoji: "🌍", label: "Traveling" },
    { emoji: "🍷", label: "Wine & Drinks" },
    { emoji: "🧘", label: "Yoga & Meditation" },
    { emoji: "⚽", label: "Sports" },
    { emoji: "🎭", label: "Theater & Culture" },
    { emoji: "🧩", label: "Board Games" },
    { emoji: "🐾", label: "Animals & Pets" },
    { emoji: "🍰", label: "Baking" },
    { emoji: "💬", label: "Deep Conversations" },
    { emoji: "😂", label: "Humor & Memes" },
    { emoji: "📷", label: "Photography" },
    { emoji: "🕯️", label: "Shabbat Traditions" },
    { emoji: "👩‍🍳", label: "Hosting" },
    { emoji: "🌿", label: "Nature & Hiking" },
    { emoji: "🧠", label: "Psychology & People" },
    { emoji: "💻", label: "Tech & Startups" },
    { emoji: "☕", label: "Coffee Lovers" },
    { emoji: "🎤", label: "Singing & Karaoke" },
    { emoji: "💡", label: "Ideas & Innovation" }
];

const LANGUAGE_OPTIONS = ISO6391.getAllCodes().map(code => ({
    value: ISO6391.getName(code),
    label: ISO6391.getName(code)
}));

const COUNTRY_OPTIONS = getCountryNames().map(name => ({
    value: getCountryCode(name),
    label: name,
    code: getCountryCode(name)
}));

// פונקציה להמרת שם מדינה לקוד מדינה
const getCountryCodeFromName = (countryName) => {
    if (!countryName) return '';
    const code = countryToAlpha2(countryName);
    return code || '';
};

const getCountryCodeSafe = (country) => {
  if (!country || typeof country !== 'string') return '';
  
  // מיפוי ידני לשמות מדינות בעייתיים
  const countryNameMap = {
    'United States of America': 'US',
    'USA': 'US',
    'United Kingdom': 'GB',
    'UK': 'GB',
    'Czechia': 'CZ',
    'Czech Republic': 'CZ'
  };
  
  const normalized = country.trim();
  if (countryNameMap[normalized]) return countryNameMap[normalized];
  
  const code = countryToAlpha2(normalized);
  return code || '';
};

export default function Profile() {
    const [languagesValue, setLanguagesValue] = useState(defaultValues.languages);
    const [interestsValue, setInterestsValue] = useState(defaultValues.interests);
    const [notesValue, setNotesValue] = useState("Add anything you think guests or hosts should know in advance – like dietary needs, personality, or expectations.");
    const [photos, setPhotos] = useState([]);
    const [location, setLocation] = useState(null);
    const [isEditingLocation, setIsEditingLocation] = useState(false);
    const [draftCity, setDraftCity] = useState("");
    const [draftCountry, setDraftCountry] = useState("");
    const [activeTab, setActiveTab] = useState('details'); // 'details' | 'pending' | 'accepted' | 'past'
    const [activePendingTab, setActivePendingTab] = useState('guest'); // 'guest' | 'host'
    const [activeAcceptedTab, setActiveAcceptedTab] = useState('guest'); // 'guest' | 'host'
    const [activePastTab, setActivePastTab] = useState('guest'); // 'guest' | 'host'
    const [isEditMode, setIsEditMode] = useState(false);
    const fileInputRef = React.useRef(null);
    const [availableCities, setAvailableCities] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [shortBioValue, setShortBioValue] = useState("");
    const [profileImg, setProfileImg] = useState("");
    const profileImgInputRef = React.useRef(null);
    const [showProfileImgModal, setShowProfileImgModal] = useState(false);
    const { currentUser } = useAuth();
    const [profileName, setProfileName] = useState("");
    const [isEditingProfileName, setIsEditingProfileName] = useState(false);
    const [draftProfileName, setDraftProfileName] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(location?.code || '');
    const [selectedCity, setSelectedCity] = useState(location?.city || '');
    const [pendingGuestRequests, setPendingGuestRequests] = useState([]);
    const [pendingHostRequests, setPendingHostRequests] = useState([]);
    const [hostPreviewProfile, setHostPreviewProfile] = useState(null);
    const [acceptedGuestRequests, setAcceptedGuestRequests] = useState([]);
    const [acceptedHostRequests, setAcceptedHostRequests] = useState([]);
    const [pastGuestRequests, setPastGuestRequests] = useState([]);
    const [pastHostRequests, setPastHostRequests] = useState([]);
    const locationRouter = useLocation();
    const [reviewInputs, setReviewInputs] = useState({});
    const [shabbotAnswers, setShabbotAnswers] = useState(null);
    const [showShabbotChat, setShowShabbotChat] = useState(false);
    const [myDinners, setMyDinners] = useState([]);
    const [dinnerToDelete, setDinnerToDelete] = useState(null);
    const navigate = useNavigate();

    // Load user data from Firestore on mount
    useEffect(() => {
        async function fetchProfile() {
            if (!currentUser) return;
            const userRef = doc(db, 'users', currentUser.uid);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
                const data = userSnap.data();
                setProfileImg(data.profileImg || "");
                setShortBioValue(data.about || "");
                setLanguagesValue(data.languages || defaultValues.languages);
                setInterestsValue(data.interests || defaultValues.interests);
                setPhotos(data.gallery || []);
                setLocation(data.location || null);
                setProfileName(data.name || "");
            }

            // Also fetch shabbotAnswers
            const answerRef = doc(db, 'shabbotAnswers', currentUser.uid);
            const answerSnap = await getDoc(answerRef);
            if (answerSnap.exists()) {
                setShabbotAnswers(answerSnap.data().answers);
            }
        }
        fetchProfile();
    }, [currentUser]);

    useEffect(() => {
        async function fetchPendingGuestRequests() {
            if (!currentUser) return;
            // שלוף את כל הבקשות של המשתמש כגסט
            const q = query(collection(db, 'dinnerRequests'), where('userId', '==', currentUser.uid), where('status', '==', 'pending'));
            const querySnapshot = await getDocs(q);
            const requests = [];
            for (const docSnap of querySnapshot.docs) {
                const data = docSnap.data();
                // שלוף את פרטי הדינר
                let dinner = null;
                if (data.dinnerId) {
                    const dinnerRef = doc(db, 'dinners', data.dinnerId);
                    const dinnerSnap = await getDoc(dinnerRef);
                    if (dinnerSnap.exists()) {
                        dinner = { id: dinnerSnap.id, ...dinnerSnap.data() };
                    }
                }
                // שלוף את פרטי ההוסט
                let hostProfile = null;
                if (dinner && dinner.hostId) {
                    const hostRef = doc(db, 'users', dinner.hostId);
                    const hostSnap = await getDoc(hostRef);
                    if (hostSnap.exists()) {
                        hostProfile = hostSnap.data();
                    }
                }
                requests.push({ ...data, id: docSnap.id, dinner, hostProfile });
            }
            setPendingGuestRequests(requests);
            // בדיקה: הדפסה ללוג
            console.log('pendingGuestRequests:', requests);
        }
        fetchPendingGuestRequests();
    }, [currentUser]);

    useEffect(() => {
        async function fetchPendingHostRequests() {
            if (!currentUser) return;
            // שלוף את כל הדינרים של המשתמש
            const dinnersSnapshot = await getDocs(query(collection(db, 'dinners'), where('hostId', '==', currentUser.uid)));
            const myDinners = dinnersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            const myDinnerIds = myDinners.map(d => d.id);
            if (myDinnerIds.length === 0) {
                setPendingHostRequests([]);
                return;
            }
            // שלוף את כל הבקשות ל-dinners שלי
            const q = query(collection(db, 'dinnerRequests'), where('dinnerId', 'in', myDinnerIds), where('status', '==', 'pending'));
            const querySnapshot = await getDocs(q);
            const requests = [];
            for (const docSnap of querySnapshot.docs) {
                const data = docSnap.data();
                // שלוף את פרטי הגסט
                let guestProfile = null;
                if (data.userId) {
                    const guestRef = doc(db, 'users', data.userId);
                    const guestSnap = await getDoc(guestRef);
                    if (guestSnap.exists()) {
                        guestProfile = guestSnap.data();
                        
                        // Fetch guest's shabbot answers for fun fact
                        const guestAnswerRef = doc(db, 'shabbotAnswers', data.userId);
                        const guestAnswerSnap = await getDoc(guestAnswerRef);
                        if (guestAnswerSnap.exists()) {
                            guestProfile.shabbotAnswers = guestAnswerSnap.data().answers;
                        }
                    }
                }
                // שלוף את פרטי הדינר
                let dinner = myDinners.find(d => d.id === data.dinnerId) || null;
                requests.push({ ...data, id: docSnap.id, guestProfile, dinner });
            }
            setPendingHostRequests(requests);
            // בדיקה: הדפסה ללוג
            console.log('pendingHostRequests:', requests);
        }
        fetchPendingHostRequests();
    }, [currentUser]);
    useEffect(() => {
        async function fetchMyDinners() {
            if (!currentUser) return;
            const q = query(collection(db, 'dinners'), where('hostId', '==', currentUser.uid));
            const querySnapshot = await getDocs(q);
            const dinners = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Filter out dinners with past dates
            const currentDate = new Date();
            currentDate.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

            const futureDinners = dinners.filter(dinner => {
                const dinnerDate = dinner.dinnerDetails?.availableDates?.[0];
                if (!dinnerDate) return false; // Skip dinners without dates

                const dinnerDateObj = new Date(dinnerDate);
                dinnerDateObj.setHours(0, 0, 0, 0); // Reset time to start of day

                return dinnerDateObj >= currentDate;
            });

            setMyDinners(futureDinners);
        }
        fetchMyDinners();
    }, [currentUser]);
    useEffect(() => {
        async function fetchAcceptedGuestRequests() {
            if (!currentUser) return;
            const q = query(collection(db, 'dinnerRequests'), where('userId', '==', currentUser.uid), where('status', '==', 'accepted'));
            const querySnapshot = await getDocs(q);
            const requests = [];
            for (const docSnap of querySnapshot.docs) {
                const data = docSnap.data();
                // שלוף את פרטי הדינר
                let dinner = null;
                if (data.dinnerId) {
                    const dinnerRef = doc(db, 'dinners', data.dinnerId);
                    const dinnerSnap = await getDoc(dinnerRef);
                    if (dinnerSnap.exists()) {
                        dinner = { id: dinnerSnap.id, ...dinnerSnap.data() };
                    }
                }
                // שלוף את פרטי ההוסט
                let hostProfile = null;
                if (dinner && dinner.hostId) {
                    const hostRef = doc(db, 'users', dinner.hostId);
                    const hostSnap = await getDoc(hostRef);
                    if (hostSnap.exists()) {
                        hostProfile = hostSnap.data();
                    }
                    
                    // Fetch shabbot answers for fun fact
                    const answerRef = doc(db, 'shabbotAnswers', dinner.hostId);
                    const answerSnap = await getDoc(answerRef);
                    if (answerSnap.exists()) {
                        dinner.shabbotAnswers = answerSnap.data().answers;
                    }
                }
                // ודא שתאריך הארוחה טרם עבר
                const dinnerDate = dinner?.dinnerDetails?.availableDates?.[0];
                const dateObj = dinnerDate ? new Date(dinnerDate) : null;
                const isPast = dateObj && dateObj < new Date();
                if (!isPast) {
                    requests.push({ ...data, id: docSnap.id, dinner, hostProfile });
                }
            }
            setAcceptedGuestRequests(requests);
        }
        fetchAcceptedGuestRequests();
    }, [currentUser]);

    useEffect(() => {
        async function fetchAcceptedHostRequests() {
            if (!currentUser) return;
            // 1. שלוף את כל הדינרים של המארח
            const dinnersQuery = query(collection(db, 'dinners'), where('hostId', '==', currentUser.uid));
            const dinnersSnapshot = await getDocs(dinnersQuery);
            const myDinnerIds = dinnersSnapshot.docs.map(doc => doc.id);

            if (myDinnerIds.length === 0) {
                setAcceptedHostRequests([]);
                return;
            }

            // 2. שלוף את כל הבקשות המאושרות עבור הדינרים של המארח
            const requestsQuery = query(collection(db, 'dinnerRequests'), where('dinnerId', 'in', myDinnerIds), where('status', '==', 'accepted'));
            const querySnapshot = await getDocs(requestsQuery);
            const requests = [];

            for (const docSnap of querySnapshot.docs) {
                const data = docSnap.data();
                let guestProfile = null;
                if (data.userId) {
                    const guestRef = doc(db, 'users', data.userId);
                    const guestSnap = await getDoc(guestRef);
                    if (guestSnap.exists()) {
                        guestProfile = { ...guestSnap.data(), id: guestSnap.id };
                    }
                    
                    // Fetch guest's shabbot answers for fun fact
                    const guestAnswerRef = doc(db, 'shabbotAnswers', data.userId);
                    const guestAnswerSnap = await getDoc(guestAnswerRef);
                    if (guestAnswerSnap.exists()) {
                        if (guestProfile) {
                            guestProfile.shabbotAnswers = guestAnswerSnap.data().answers;
                        }
                    }
                }

                let dinner = null;
                if(data.dinnerId) {
                    const dinnerRef = doc(db, 'dinners', data.dinnerId);
                    const dinnerSnap = await getDoc(dinnerRef);
                    if(dinnerSnap.exists()) {
                        dinner = { ...dinnerSnap.data(), id: dinnerSnap.id };
                    }
                }

                // ודא שתאריך הארוחה טרם עבר
                const dinnerDate = dinner?.dinnerDetails?.availableDates?.[0];
                const dateObj = dinnerDate ? new Date(dinnerDate) : null;
                const isPast = dateObj && dateObj < new Date();

                if (!isPast) {
                    requests.push({ ...data, id: docSnap.id, guestProfile, dinner });
                }
            }
            setAcceptedHostRequests(requests);
        }
        fetchAcceptedHostRequests();
    }, [currentUser]);

    useEffect(() => {
        async function fetchPastGuestRequests() {
            if (!currentUser) return;
            // שלוף את כל הבקשות של המשתמש כגסט
            const q = query(collection(db, 'dinnerRequests'), where('userId', '==', currentUser.uid), where('status', '==', 'accepted'));
            const querySnapshot = await getDocs(q);
            const requests = [];
            for (const docSnap of querySnapshot.docs) {
                const data = docSnap.data();
                // שלוף את פרטי הדינר
                let dinner = null;
                if (data.dinnerId) {
                    const dinnerRef = doc(db, 'dinners', data.dinnerId);
                    const dinnerSnap = await getDoc(dinnerRef);
                    if (dinnerSnap.exists()) {
                        dinner = { id: dinnerSnap.id, ...dinnerSnap.data() };
                    }
                }
                // שלוף את פרטי ההוסט
                let hostProfile = null;
                if (dinner && dinner.hostId) {
                    const hostRef = doc(db, 'users', dinner.hostId);
                    const hostSnap = await getDoc(hostRef);
                    if (hostSnap.exists()) {
                        hostProfile = hostSnap.data();
                    }
                }
                // בדוק אם הארוחה עברה
                const dinnerDate = dinner?.dinnerDetails?.availableDates?.[0];
                if (dinnerDate && new Date(dinnerDate) < new Date() && !data.feedbackNotificationSent) {
                    // שלח התראה אם אין כבר אחת כזו (לא משנה אם היא נקראה או לא)
                    const notifQuery = query(
                        collection(db, 'notifications'),
                        where('userId', '==', currentUser.uid),
                        where('dinnerId', '==', dinner.id),
                        where('type', '==', 'feedback'),
                        where('feedbackRole', '==', 'guest')
                    );
                    const notifSnapshot = await getDocs(notifQuery);
                    if (notifSnapshot.empty) {
                        await addDoc(collection(db, 'notifications'), {
                            userId: currentUser.uid,
                            requestId: docSnap.id,
                            dinnerId: dinner.id,
                            type: 'feedback',
                            feedbackRole: 'guest',
                            message: 'Dinner completed! Please leave feedback about your recent Shabbat dinner.',
                            link: '/profile?tab=past&pastTab=guest',
                            createdAt: new Date().toISOString(),
                            isRead: false
                        });
                        // עדכן את הבקשה שסומנה כהתראה נשלחה
                        await updateDoc(doc(db, 'dinnerRequests', docSnap.id), { feedbackNotificationSent: true });
                    }
                }
                requests.push({ ...data, id: docSnap.id, dinner, hostProfile });
            }
            setPastGuestRequests(requests);
        }
        fetchPastGuestRequests();
    }, [currentUser]);

    useEffect(() => {
        async function fetchPastHostRequests() {
            if (!currentUser) return;
            // 1. שלוף את כל הדינרים של המארח
            const dinnersQuery = query(collection(db, 'dinners'), where('hostId', '==', currentUser.uid));
            const dinnersSnapshot = await getDocs(dinnersQuery);
            const myDinnerIds = dinnersSnapshot.docs.map(doc => doc.id);

            if (myDinnerIds.length === 0) {
                setPastHostRequests([]);
                return;
            }

            // 2. שלוף את כל הבקשות המאושרות עבור הדינרים של המארח
            const requestsQuery = query(collection(db, 'dinnerRequests'), where('dinnerId', 'in', myDinnerIds), where('status', '==', 'accepted'));
            const querySnapshot = await getDocs(requestsQuery);
            const requests = [];

            for (const docSnap of querySnapshot.docs) {
                const data = docSnap.data();
                // שלוף את פרטי הגסט
                let guestProfile = null;
                if (data.userId) {
                    const guestRef = doc(db, 'users', data.userId);
                    const guestSnap = await getDoc(guestRef);
                    if (guestSnap.exists()) {
                        guestProfile = guestSnap.data();
                    }
                }
                // שלוף את פרטי הדינר
                let dinner = dinnersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).find(d => d.id === data.dinnerId) || null;
                // בדוק אם הארוחה עברה
                const dinnerDate = dinner?.dinnerDetails?.availableDates?.[0];
                if (dinnerDate && new Date(dinnerDate) < new Date() && !data.feedbackNotificationSent) {
                    // שלח התראה אם אין כבר אחת כזו (לא משנה אם היא נקראה או לא)
                    const notifQuery = query(
                        collection(db, 'notifications'),
                        where('userId', '==', currentUser.uid),
                        where('dinnerId', '==', dinner.id),
                        where('type', '==', 'feedback'),
                        where('feedbackRole', '==', 'host')
                    );
                    const notifSnapshot = await getDocs(notifQuery);
                    if (notifSnapshot.empty) {
                        await addDoc(collection(db, 'notifications'), {
                            userId: currentUser.uid,
                            requestId: docSnap.id,
                            dinnerId: dinner.id,
                            type: 'feedback',
                            feedbackRole: 'host',
                            message: 'Dinner completed! Please leave feedback about your recent Shabbat dinner.',
                            link: '/profile?tab=past&pastTab=host',
                            createdAt: new Date().toISOString(),
                            isRead: false
                        });
                        // עדכן את הבקשה שסומנה כהתראה נשלחה
                        await updateDoc(doc(db, 'dinnerRequests', docSnap.id), { feedbackNotificationSent: true });
                    }
                }
                requests.push({ ...data, id: docSnap.id, guestProfile, dinner });
            }
            setPastHostRequests(requests);
        }
        fetchPastHostRequests();
    }, [currentUser]);

    useEffect(() => {
        // Deep link: if URL contains ?tab=pending&pendingTab=host, open the correct tab
        const params = new URLSearchParams(locationRouter.search);
        const tab = params.get('tab');
        const acceptedTab = params.get('acceptedTab');
        const pendingTab = params.get('pendingTab');
        const pastTab = params.get('pastTab');
        const preview = params.get('preview');
        
        if (tab) setActiveTab(tab);
        if (acceptedTab) setActiveAcceptedTab(acceptedTab);
        if (pendingTab) setActivePendingTab(pendingTab);
        if (pastTab) setActivePastTab(pastTab);
        
        // אם יש preview=true, פתח את ה-preview modal
        if (preview === 'true') {
            setShowPreview(true);
        }
    }, [locationRouter.search]);

    // Save all profile fields to Firestore
    async function handleSaveProfile() {
        if (!currentUser) return;
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, {
            profileImg,
            about: shortBioValue,
            languages: languagesValue,
            interests: interestsValue,
            gallery: photos,
            location
        });
        setIsEditMode(false);
    }

    // Save individual fields
    async function handleFieldSave(field, value) {
        if (!currentUser) return;
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, { [field]: value });
    }

    const countries = [
        { name: "Israel", code: "IL", flag: "🇮🇱", cities: ["Jerusalem", "Tel Aviv", "Haifa", "Beer Sheva", "Eilat"] },
        { name: "France", code: "FR", flag: "🇫🇷", cities: ["Paris", "Lyon", "Marseille", "Nice", "Bordeaux"] },
        { name: "United States", code: "US", flag: "🇺🇸", cities: ["New York", "Los Angeles", "Chicago", "Miami", "San Francisco"] },
        { name: "Germany", code: "DE", flag: "🇩🇪", cities: ["Berlin", "Munich", "Hamburg", "Frankfurt", "Cologne"] },
        { name: "United Kingdom", code: "GB", flag: "🇬🇧", cities: ["London", "Manchester", "Birmingham", "Edinburgh", "Glasgow"] },
    ];

    const handlePhotoClick = () => {
        fileInputRef.current.click();
    };

    const handlePhotoChange = async (e) => {
        const files = Array.from(e.target.files);
        if (!currentUser) return;
        try {
            const urls = await Promise.all(files.map(file => uploadToCloudinary(file)));
            setPhotos(prev => {
                const newPhotos = [...prev, ...urls];
                handleFieldSave('gallery', newPhotos);
                return newPhotos;
            });
        } catch (error) {
            alert('העלאת אחת או יותר מהתמונות נכשלה');
        }
    };

    const handleCountryChange = (e) => {
        const countryCode = e.target.value;
        setDraftCountry(countryCode);
        setDraftCity(""); // Reset city when country changes
        const selectedCountry = countries.find(c => c.code === countryCode);
        setAvailableCities(selectedCountry ? selectedCountry.cities : []);
    };

    const handleLocationSave = () => {
        const countryObj = COUNTRY_OPTIONS.find(c => c.value === draftCountry);
        const countryName = countryObj ? countryObj.label : draftCountry;
        const code = draftCountry;
        const flag = draftCountry;
        const newLocation = {
            city: draftCity,
            country: countryName,
            code,
            flag
        };
        setLocation(newLocation);
        handleFieldSave('location', newLocation);
        setIsEditingLocation(false);
        setDraftCountry('');
        setDraftCity('');
    };

    const handleProfileImgClick = () => {
        if (isEditMode && profileImgInputRef.current) {
            profileImgInputRef.current.click();
        } else if (!isEditMode) {
            setShowProfileImgModal(true);
        }
    };

    const handleProfileImgChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!currentUser) return;
            try {
                setIsUploading(true);
                const url = await uploadToCloudinary(file);
                setProfileImg(url);
                await handleFieldSave('profileImg', url);
            } catch (error) {
                console.error('שגיאה בהעלאת תמונה:', error);
                alert(error.message || 'העלאת התמונה נכשלה');
                setProfileImg('');
            } finally {
                setIsUploading(false);
            }
        }
    };

    const cityOptions = selectedCountry
        ? City.getCitiesOfCountry(selectedCountry).map(city => ({
            value: city.name,
            label: city.name
        }))
        : [];

    const handleApproveRequest = async (requestId) => {
        const requestToMove = pendingHostRequests.find(r => r.id === requestId);
        await updateDoc(doc(db, 'dinnerRequests', requestId), { status: 'accepted' });
        setPendingHostRequests(prev => prev.filter(r => r.id !== requestId));
        if (requestToMove) {
            setAcceptedHostRequests(prev => [...prev, { ...requestToMove, status: 'accepted' }]);
            
            // צור התראה למתארח שהבקשה אושרה
            await addDoc(collection(db, 'notifications'), {
                userId: requestToMove.userId,
                requestId: requestId,
                type: 'approval',
                message: 'You have an update from the host',
                link: '/profile?tab=accepted&acceptedTab=guest',
                createdAt: new Date().toISOString(),
                isRead: false
            });
            // פופ-אפ חיווי למארח
            toast.success(
              <div style={{ textAlign: 'center', width: '100%' }}>
                <b>Request approved!</b><br/>
                The guest has been notified.<br/>
                You can view or contact them in Accepted Dinners.
              </div>,
              { duration: 5000, position: 'top-center' }
            );
        }
        // מחיקת התראות מסוג reminder עבור הבקשה הזו
        const notifQuery = query(collection(db, 'notifications'), where('requestId', '==', requestId), where('type', '==', 'reminder'));
        const notifSnapshot = await getDocs(notifQuery);
        notifSnapshot.forEach(async (notifDoc) => {
            await deleteDoc(doc(db, 'notifications', notifDoc.id));
        });
    };

    const handleDeclineRequest = async (requestId) => {
        await updateDoc(doc(db, 'dinnerRequests', requestId), { status: 'declined' });
        setPendingHostRequests(prev => prev.filter(r => r.id !== requestId));
        // מחיקת התראות מסוג reminder עבור הבקשה הזו
        const notifQuery = query(collection(db, 'notifications'), where('requestId', '==', requestId), where('type', '==', 'reminder'));
        const notifSnapshot = await getDocs(notifQuery);
        notifSnapshot.forEach(async (notifDoc) => {
            await deleteDoc(doc(db, 'notifications', notifDoc.id));
        });
    };

    // Add a function to handle review submission
    const handleReviewChange = (requestId, value) => {
        setReviewInputs(prev => ({ ...prev, [requestId]: value }));
    };

    const handleReviewSubmit = async (requestId, hostId) => {
        const reviewText = reviewInputs[requestId];
        if (!reviewText) {
            return;
        }

        const reviewData = {
            text: reviewText,
            authorId: currentUser.uid,
            createdAt: new Date()
        };

        await updateDoc(doc(db, 'dinnerRequests', requestId), { guestFeedback: reviewData });

        // שליחת התראה למארח
        if (hostId) {
            const newNotification = {
                userId: hostId,
                type: 'feedback-received',
                message: 'You received feedback from your guest! Click to view it on your profile.',
                link: '/profile?preview=true',
                createdAt: new Date().toISOString(),
                isRead: false
            };
            await addDoc(collection(db, 'notifications'), newNotification);
        }

        // Optimistically update the UI
        setPastGuestRequests(prev => prev.map(d => d.id === requestId ? {...d, guestFeedback: reviewData} : d));
        setReviewInputs(prev => ({ ...prev, [requestId]: '' }));
        toast.success("Your feedback was sent to the host!");
    };

    const handleHostReviewSubmit = async (requestId, guestId) => {
        const reviewText = reviewInputs[requestId];
        if (!reviewText) {
            return;
        }

        const reviewData = {
            text: reviewText,
            authorId: currentUser.uid,
            createdAt: new Date()
        };

        await updateDoc(doc(db, 'dinnerRequests', requestId), { hostFeedback: reviewData });

        // שליחת התראה לאורח
        if (guestId) {
            const newNotification = {
                userId: guestId,
                type: 'feedback-received',
                message: 'You received feedback from your host! Click to view it on your profile.',
                link: '/profile?preview=true',
                createdAt: new Date().toISOString(),
                isRead: false
            };
            await addDoc(collection(db, 'notifications'), newNotification);
        }

        // Optimistically update the UI
        setPastHostRequests(prev => prev.map(d => d.id === requestId ? {...d, hostFeedback: reviewData} : d));
        setReviewInputs(prev => ({ ...prev, [requestId]: '' }));
        toast.success("Your feedback was sent to the guest!");
    };

    const renderPastDinners = (dinners, isHostView) => {
        if (!dinners || dinners.length === 0) {
            return (
                <p className="feedback-prompt-text">No past dinners to display.</p>
            );
        }

        return (
            <div className="dinner-card-list">
                {dinners.map((req) => {
                    const dinnerDate = req.dinner?.dinnerDetails?.availableDates?.[0];
                    const dateObj = dinnerDate ? new Date(dinnerDate) : null;
                    const isPast = dateObj && dateObj < new Date();

                    if (!isPast) return null;

                    const hasReview = req.guestFeedback && req.guestFeedback.text;

                    return (
                        <div key={req.id} className="dinner-card-v2-scaled" style={{ background: '#fff', borderRadius: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', padding: '32px 36px', marginBottom: 24, maxWidth: 600 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 18, marginBottom: 12 }}>
                                <img src={req.guestProfile?.profileImg || '/blankprofile.png'} alt={req.guestProfile?.name || 'Guest'} style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', background: '#f7f7f7', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }} />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, fontSize: 20, color: '#15316b', marginBottom: 2 }}>{req.guestProfile?.name}</div>
                                    <div style={{ color: '#888', fontSize: 15 }}>{req.dinner?.address?.city}{req.dinner?.address?.city && req.dinner?.address?.country ? ', ' : ''}{req.dinner?.address?.country}</div>
                                    <div style={{ color: '#22685b', fontSize: 15, marginTop: 2 }}><b>Date:</b> {dateObj ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</div>
                                </div>
                            </div>

                            {hasReview ? (
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 8, color: '#15316b' }}>Your feedback:</div>
                                    <p style={{fontStyle: 'italic', color: '#555'}}>"{req.guestFeedback.text}"</p>
                                </div>
                            ) : (
                                <>
                                    <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 8, color: '#15316b' }}>What did you love most about the Shabbat dinner?</div>
                                    <textarea
                                        value={reviewInputs[req.id] || ''}
                                        onChange={e => handleReviewChange(req.id, e.target.value)}
                                        placeholder="A great vibe? Good food? Wonderful people? Share it with us"
                                        style={{ width: '100%', minHeight: 60, borderRadius: 10, border: '1px solid #e0e0e0', padding: 10, fontSize: 15, marginBottom: 10 }}
                                    />
                                    <button
                                        onClick={() => handleReviewSubmit(req.id, req.dinner.hostId)}
                                        disabled={!reviewInputs[req.id]}
                                        style={{ background: '#4ecb8f', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 22px', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}
                                    >
                                        Submit Feedback
                                    </button>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // שליחת התראה למארח (host) על בקשה ממתינה
    async function sendReminderNotification(request) {
        console.log('clicked reminder', request);
        toast.success('Reminder sent to the host!');
        if (!request || !request.hostId || !request.id) return;
        await addDoc(collection(db, 'notifications'), {
            userId: request.hostId,
            requestId: request.id,
            type: 'reminder',
            createdAt: new Date().toISOString(),
            message: 'Reminder: You have a pending dinner request awaiting your approval.',
            link: '/profile?tab=pending&pendingTab=host'
        });
    }

    return (
        <>
            <div className="profile-page">
                <div className="container-fluid">
                    <div className="row g-0">
                        <aside className="profile-sidebar col-md-4 mb-4">
                            <ProfileSidebar
                                location={location}
                                setIsEditingLocation={setIsEditingLocation}
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                isEditMode={isEditMode}
                                setIsEditMode={setIsEditMode}
                                profileImg={profileImg}
                                onProfileImgClick={handleProfileImgClick}
                                profileImgInputRef={profileImgInputRef}
                                onProfileImgChange={handleProfileImgChange}
                                profileName={profileName}
                                setProfileName={setProfileName}
                                handleFieldSave={handleFieldSave}
                                isUploading={isUploading}
                            />
                        </aside>
                        <main className="profile-content col-md-8">
                            {activeTab === 'details' && (
                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: 18 }}>
                                    <div style={{ position: 'relative' }}>
                                        <button
                                            className="profile-personalize-btn"
                                            onClick={() => setShowShabbotChat(true)}
                                        >
                                            Speak with Shabbot again
                                        </button>
                                    </div>
                                    <button
                                        className="profile-preview-btn"
                                        onClick={() => setShowPreview(true)}
                                    >
                                        My profile preview
                                    </button>
                                </div>
                            )}
                            {isEditingLocation && (
                                <div className="location-modal" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
                                    <div style={{ background: '#fff', padding: 24, borderRadius: 12, minWidth: 300 }}>
                                        <h3 style={{ marginBottom: 12 }}>Add your location</h3>
                                        <CreatableSelect
                                            options={COUNTRY_OPTIONS}
                                            value={COUNTRY_OPTIONS.find(c => c.value === draftCountry) || (draftCountry ? { value: draftCountry, label: draftCountry } : null)}
                                            onChange={opt => {
                                                setDraftCountry(opt.value);
                                                setDraftCity("");
                                                setAvailableCities(opt.value ? City.getCitiesOfCountry(opt.value).map(city => ({ value: city.name, label: city.name })) : []);
                                            }}
                                            getOptionLabel={opt =>
                                                opt.code
                                                    ? (<><ReactCountryFlag countryCode={opt.code} svg style={{ width: 18, height: 14, marginRight: 8, verticalAlign: 'middle' }} />{opt.label}</>)
                                                    : opt.label
                                            }
                                            getOptionValue={opt => opt.value}
                                            filterOption={(option, input) => {
                                                const label = typeof option.label === 'string'
                                                    ? option.label
                                                    : (option.data && typeof option.data.label === 'string'
                                                        ? option.data.label
                                                        : (option.value || ''));
                                                return label.toLowerCase().includes(input.toLowerCase());
                                            }}
                                            placeholder="Select or type a country..."
                                            menuPortalTarget={document.body}
                                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        />
                                        <div style={{ height: 16 }} />
                                        <CreatableSelect
                                            options={availableCities}
                                            value={draftCity ? { value: draftCity, label: draftCity } : null}
                                            onChange={opt => setDraftCity(opt.value)}
                                            placeholder="Select or type a city..."
                                            isDisabled={!draftCountry}
                                            menuPortalTarget={document.body}
                                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        />
                                        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 18 }}>
                                            <button onClick={handleLocationSave} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#1a6fa4', color: '#fff' }}>Save</button>
                                            <button onClick={() => setIsEditingLocation(false)} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#eee', color: '#333' }}>Cancel</button>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {activeTab === 'details' && (
                                <>
                                    <ProfileSection
                                        title="Short Bio"
                                        value={shortBioValue}
                                        editable={isEditMode}
                                        alwaysBlack
                                        onChange={val => {
                                            setShortBioValue(val);
                                            handleFieldSave('about', val);
                                        }}
                                    />
                                    <ProfileSection
                                        title="Languages spoken"
                                        value={languagesValue}
                                        editable={isEditMode}
                                        defaultValue={defaultValues.languages}
                                        onChange={val => {
                                            setLanguagesValue(val);
                                            handleFieldSave('languages', val);
                                        }}
                                        renderEdit={({ value, onChange }) => (
                                            <CreatableSelect
                                                isMulti
                                                options={LANGUAGE_OPTIONS}
                                                value={
                                                    value
                                                        ? value.split(',').map(s => {
                                                            const opt = LANGUAGE_OPTIONS.find(o => o.value === s.trim());
                                                            return opt || { value: s.trim(), label: s.trim() };
                                                        })
                                                        : []
                                                }
                                                onChange={opts => onChange(opts.map(o => o.value).join(', '))}
                                                placeholder="Select or type any language..."
                                                className="languages-select"
                                                menuPortalTarget={document.body}
                                                styles={{
                                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                                }}
                                            />
                                        )}
                                        renderValue={value =>
                                            value
                                                ? value.split(',').map(s => s.trim()).filter(Boolean).join(', ')
                                                : ''
                                        }
                                    />
                                    <ProfileSection
                                        title="Interests"
                                        value={interestsValue}
                                        editable={isEditMode}
                                        defaultValue={defaultValues.interests}
                                        onChange={val => {
                                            setInterestsValue(val);
                                            handleFieldSave('interests', val);
                                        }}
                                        renderEdit={({ value, onChange }) => (
                                            <div className="interests-grid2">
                                                {INTERESTS.map(interest => (
                                                    <button
                                                        key={interest.label}
                                                        type="button"
                                                        className={`interest-btn${value && value.split(',').map(s => s.trim()).includes(interest.label) ? ' selected' : ''}`}
                                                        onClick={e => {
                                                            e.preventDefault();
                                                            let arr = value ? value.split(',').map(s => s.trim()).filter(Boolean) : [];
                                                            if (arr.includes(interest.label)) {
                                                                arr = arr.filter(i => i.trim() !== interest.label);
                                                            } else {
                                                                arr.push(interest.label);
                                                            }
                                                            onChange(arr.join(', '));
                                                        }}
                                                    >
                                                        <span className="emoji">{interest.emoji}</span> {interest.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        renderValue={value => (
                                            <div className="interests-grid2">
                                                {value && value.split(',').map(s => s.trim()).filter(Boolean).map(label => {
                                                    const found = INTERESTS.find(i => i.label === label);
                                                    return found ? (
                                                        <span key={label} className="interest-btn selected" style={{ cursor: 'default' }}>
                                                            <span className="emoji">{found.emoji}</span> {found.label}
                                                        </span>
                                                    ) : null;
                                                })}
                                            </div>
                                        )}
                                    />
                                    <div className="profile-section">
                                        <div className="profile-section-header">
                                            Add Photos
                                        </div>
                                        <div className="profile-photos">
                                            {photos.map((src, idx) => (
                                                <div key={idx} style={{ position: 'relative', display: 'inline-block', marginRight: 8 }}>
                                                    <img
                                                        src={src}
                                                        alt={`Uploaded ${idx + 1}`}
                                                        className="profile-photo-preview"
                                                        style={{ width: 120, height: 120, borderRadius: 20, objectFit: "cover" }}
                                                    />
                                                    <button
                                                        onClick={() => setPhotos(photos.filter((_, i) => i !== idx))}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 7,
                                                            right: 7,
                                                            background: 'rgba(220,220,220,0.85)',
                                                            color: '#444',
                                                            border: 'none',
                                                            borderRadius: '50%',
                                                            width: 24,
                                                            height: 24,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'pointer',
                                                            fontSize: 16,
                                                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                                                        }}
                                                        title="Remove image"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ))}
                                            <button className="profile-photo-add" onClick={handlePhotoClick}>
                                                <FiPlus size={28} />
                                            </button>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                style={{ display: "none" }}
                                                ref={fileInputRef}
                                                onChange={handlePhotoChange}
                                                multiple
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            {activeTab === 'pending' && (
                                <div className="pending-requests-section">
                                    <h2 className="pending-title">Dinners Awaiting Approval</h2>
                                    <div className="pending-desc">
                                        Here you can view all of your pending dinner requests – whether you've sent a request as a guest or received one as a host.
                                    </div>
                                    <div className="pending-card-tabs-container">
                                        <div className="pending-tabs">
                                            <div
                                                className={`pending-tab${activePendingTab === 'guest' ? ' active' : ''}`}
                                                onClick={() => setActivePendingTab('guest')}
                                            >
                                                I'm a Guest
                                            </div>
                                            <div
                                                className={`pending-tab${activePendingTab === 'host' ? ' active' : ''}`}
                                                onClick={() => setActivePendingTab('host')}
                                            >
                                                I'm a Host
                                            </div>
                                        </div>
                                        <div className="pending-card-content">
                                            <div className="dinner-card-list">
                                                {activePendingTab === 'guest' && pendingGuestRequests.length > 0 && pendingGuestRequests.map((req) => (
                                                    <div key={req.id} className="dinner-card-v2-scaled">
                                                        <ProfileDinnerCard
                                                            hostProfile={req.hostProfile}
                                                            dinner={req.dinner}
                                                            request={req}
                                                            onReminder={() => sendReminderNotification(req)}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                            {activePendingTab === 'host' && pendingHostRequests.length > 0 && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: 24, alignItems: 'flex-start' }}>
                                                    {pendingHostRequests.map((req) => (
                                                        <div key={req.id} style={{
                                                            background: '#f7f7f7',
                                                            borderRadius: 18,
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                                                            padding: '28px 32px',
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            gap: 32,
                                                            maxWidth: 540,
                                                            border: '1px solid #e0e0e0',
                                                            width: '540px',
                                                            margin: 0
                                                        }}>
                                                            {/* תמונת פרופיל של הגסט */}
                                                            <img src={req.guestProfile?.profileImg || '/blankprofile.png'} alt={req.guestProfile?.name || 'Guest'} style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', marginRight: 18, background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }} />
                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                <div style={{ fontWeight: 700, fontSize: 22, color: '#15316b', marginBottom: 2, cursor: 'pointer', textAlign: 'left', fontFamily: 'Georgia, serif' }}
                                                                    onClick={() => setHostPreviewProfile(req.guestProfile)}>{req.guestProfile?.name || 'Guest'}</div>
                                                                <div style={{ color: '#888', fontSize: 15, marginBottom: 8, textAlign: 'left' }}>
                                                                    {req.guestProfile?.location?.city}{req.guestProfile?.location?.city && req.guestProfile?.location?.country ? ', ' : ''}{req.guestProfile?.location?.country}
                                                                    {req.guestProfile?.location?.country && (
                                                                        <ReactCountryFlag 
                                                                            countryCode={getCountryCodeSafe(req.guestProfile?.location?.country)} 
                                                                            svg 
                                                                            style={{ fontSize: '1em', marginLeft: 6, verticalAlign: 'middle' }} 
                                                                        />
                                                                    )}
                                                                </div>
                                                                <div style={{ fontSize: 16, marginBottom: 6, textAlign: 'left' }}><b>Guests:</b> {req.guestsCount}</div>
                                                                <div style={{ fontSize: 16, marginBottom: 6, textAlign: 'left' }}><b>Languages:</b> {req.guestProfile?.languages}</div>
                                                                {req.message && <div style={{ fontSize: 16, marginBottom: 6, textAlign: 'left' }}><b>Message:</b> {req.message}</div>}
                                                                {req.needs && <div style={{ fontSize: 16, marginBottom: 6, textAlign: 'left' }}><b>Needs:</b> {req.needs}</div>}
                                                            </div>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-end', minWidth: 120, width: 120 }}>
                                                                <button style={{ background: '#4ecb8f', color: '#fff', border: 'none', borderRadius: 20, padding: '10px 18px', fontWeight: 700, fontSize: 16, cursor: 'pointer', marginBottom: 4, width: '100%', minWidth: 0, textAlign: 'center' }} onClick={() => handleApproveRequest(req.id)}>Approve</button>
                                                                <button style={{ background: '#e57373', color: '#fff', border: 'none', borderRadius: 20, padding: '10px 18px', fontWeight: 700, fontSize: 16, cursor: 'pointer', width: '100%', minWidth: 0, textAlign: 'center' }} onClick={() => handleDeclineRequest(req.id)}>Decline</button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {/* Preview modal */}
                                                    <PreviewProfileModal
                                                        open={!!hostPreviewProfile}
                                                        onClose={() => setHostPreviewProfile(null)}
                                                        profile={hostPreviewProfile ? {
                                                            uid: hostPreviewProfile.uid || hostPreviewProfile.id,
                                                            avatar: hostPreviewProfile.profileImg,
                                                            name: hostPreviewProfile.name,
                                                            city: hostPreviewProfile.location?.city,
                                                            country: hostPreviewProfile.location?.country,
                                                            bio: hostPreviewProfile.about,
                                                            languages: hostPreviewProfile.languages,
                                                            interests: hostPreviewProfile.interests,
                                                            gallery: hostPreviewProfile.gallery,
                                                            shabbotAnswers: hostPreviewProfile.shabbotAnswers
                                                        } : {}}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {activeTab === 'accepted' && (
                                <div className="pending-requests-section">
                                    <h2 className="pending-title">Confirmed Dinners</h2>
                                    <div className="pending-desc">
                                        Here you can view all of your confirmed dinners - whether you're the host or the guest.
                                    </div>
                                    <div className="pending-card-tabs-container">
                                        <div className="pending-tabs">
                                            <div
                                                className={`pending-tab${activeAcceptedTab === 'guest' ? ' active' : ''}`}
                                                onClick={() => setActiveAcceptedTab('guest')}
                                            >
                                                I'm a Guest
                                            </div>
                                            <div
                                                className={`pending-tab${activeAcceptedTab === 'host' ? ' active' : ''}`}
                                                onClick={() => setActiveAcceptedTab('host')}
                                            >
                                                I'm a Host
                                            </div>
                                        </div>
                                        <div className="pending-card-content">
                                            <div className="dinner-card-list">
                                                {activeAcceptedTab === 'guest' && acceptedGuestRequests.length > 0 && (
                                                    acceptedGuestRequests.map((req) => (
                                                        <div key={req.id} className="dinner-card-v2-scaled">
                                                            <ProfileDinnerCard
                                                                hostProfile={req.hostProfile}
                                                                dinner={req.dinner}
                                                                request={req}
                                                                actionsType="accepted"
                                                            />
                                                        </div>
                                                    ))
                                                )}
                                                {activeAcceptedTab === 'host' && acceptedHostRequests.length > 0 && (
                                                    acceptedHostRequests.map((req) => (
                                                        <div key={req.id} className="dinner-card-v2-scaled">
                                                            <ProfileDinnerCard
                                                                hostProfile={req.guestProfile}
                                                                dinner={req.dinner}
                                                                request={req}
                                                                actionsType="accepted"
                                                                hideKosherLevel={true}
                                                                isHostView={true}
                                                            />
                                                        </div>
                                                    ))
                                                )}
                                                {activeAcceptedTab === 'host' && acceptedHostRequests.length === 0 && (
                                                    <p style={{ textAlign: 'center', marginTop: 20, color: '#666' }}>You have no accepted dinners as a host yet.</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {activeTab === 'past' && (
                                <div className="pending-requests-section">
                                    <h2 className="pending-title">Share your feedback</h2>
                                    <div className="pending-desc">
                                        We'd love to hear about your Shabbat experience as a guest or host!
                                    </div>
                                    <div className="pending-card-tabs-container">
                                        <div className="pending-tabs">
                                            <div
                                                className={`pending-tab${activePastTab === 'guest' ? ' active' : ''}`}
                                                onClick={() => setActivePastTab('guest')}
                                            >
                                                I'm a Guest
                                            </div>
                                            <div
                                                className={`pending-tab${activePastTab === 'host' ? ' active' : ''}`}
                                                onClick={() => setActivePastTab('host')}
                                            >
                                                I'm a Host
                                            </div>
                                        </div>
                                        <div className="pending-card-content">
                                            <div className="dinner-card-list">
                                                {activePastTab === 'guest' && pastGuestRequests.length > 0 && (
                                                    pastGuestRequests.map((req) => {
                                                        // This check is now done in fetchPastGuestRequests, but we can keep it for safety
                                                        const dinnerDate = req.dinner?.dinnerDetails?.availableDates?.[0];
                                                        const dateObj = dinnerDate ? new Date(dinnerDate) : null;
                                                        const isPast = dateObj && dateObj < new Date();
                                                        if (!isPast) return null;

                                                        const hasReview = req.guestFeedback && req.guestFeedback.text;

                                                        return (
                                                            <div key={req.id} className="dinner-card-v2-scaled past-dinner-card" style={{ background: '#fff', borderRadius: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', padding: '32px 36px', marginBottom: 24, maxWidth: 600 }}>
                                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18, marginBottom: 12 }}>
                                                                    <img src={req.hostProfile?.profileImg || '/blankprofile.png'} alt={req.hostProfile?.name || 'Host'} style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }} />
                                                                    <div style={{ flex: 1, textAlign: 'left' }}>
                                                                        <div 
                                                                            style={{ fontWeight: 700, fontSize: 22, color: '#15316b', marginBottom: 2, textAlign: 'left', fontFamily: 'Georgia, serif', cursor: 'pointer' }}
                                                                            onClick={() => setHostPreviewProfile(req.hostProfile)}
                                                                        >
                                                                            {req.hostProfile?.name}
                                                                        </div>
                                                                        <div style={{ color: '#888', fontSize: 15, textAlign: 'left' }}>
                                                                            {req.dinner?.address?.city}{req.dinner?.address?.city && req.dinner?.address?.country ? ', ' : ''}{req.dinner?.address?.country}
                                                                            {req.dinner?.address?.country && (
                                                                                <ReactCountryFlag 
                                                                                    countryCode={getCountryCodeSafe(req.dinner?.address?.country)} 
                                                                                    svg 
                                                                                    style={{ fontSize: '1em', marginLeft: 6, verticalAlign: 'middle' }} 
                                                                                />
                                                                            )}
                                                                        </div>
                                                                        <div style={{ color: '#22685b', fontSize: 15, marginTop: 2, textAlign: 'left' }}><b>Date:</b> {dateObj ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</div>
                                                                    </div>
                                                                </div>

                                                                {hasReview ? (
                                                                    <div>
                                                                        <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 8, color: '#15316b' }}>Your feedback:</div>
                                                                        <p style={{fontStyle: 'italic', color: '#555'}}>"{req.guestFeedback.text}"</p>
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 8, color: '#15316b' }}>What did you love most about the Shabbat dinner?</div>
                                                                        <textarea
                                                                            value={reviewInputs[req.id] || ''}
                                                                            onChange={e => handleReviewChange(req.id, e.target.value)}
                                                                            placeholder="A great vibe? Good food? Wonderful people? Share it with us"
                                                                            style={{ width: '100%', minHeight: 60, borderRadius: 10, border: '1px solid #e0e0e0', padding: 10, fontSize: 15, marginBottom: 10 }}
                                                                        />
                                                                        <button
                                                                            className="submit-feedback-btn"
                                                                                                                    onClick={() => handleReviewSubmit(req.id, req.dinner.hostId)}
                                                                            disabled={!reviewInputs[req.id]}
                                                                        >
                                                                            Submit Feedback
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                                )}
                                                {activePastTab === 'host' && pastHostRequests.map((req) => {
                                                    const dinnerDate = req.dinner?.dinnerDetails?.availableDates?.[0];
                                                    const dateObj = dinnerDate ? new Date(dinnerDate) : null;
                                                    const hasReview = req.hostFeedback && req.hostFeedback.text;

                                                    return (
                                                        <div key={req.id} className="dinner-card-v2-scaled past-dinner-card" style={{ background: '#fff', borderRadius: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', padding: '32px 36px', marginBottom: 24, maxWidth: 600 }}>
                                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18, marginBottom: 12 }}>
                                                                <img src={req.guestProfile?.profileImg || '/blankprofile.png'} alt={req.guestProfile?.name || 'Guest'} style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, background: '#f7f7f7', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }} />
                                                                <div style={{ flex: 1, textAlign: 'left' }}>
                                                                    <div 
                                                                        style={{ fontWeight: 700, fontSize: 22, color: '#15316b', marginBottom: 2, textAlign: 'left', fontFamily: 'Georgia, serif', cursor: 'pointer' }}
                                                                        onClick={() => setHostPreviewProfile(req.guestProfile)}
                                                                    >
                                                                        {req.guestProfile?.name}
                                                                    </div>
                                                                    <div style={{ color: '#888', fontSize: 15, textAlign: 'left' }}>
                                                                        {req.dinner?.address?.city}{req.dinner?.address?.city && req.dinner?.address?.country ? ', ' : ''}{req.dinner?.address?.country}
                                                                        {req.dinner?.address?.country && (
                                                                            <ReactCountryFlag 
                                                                                countryCode={getCountryCodeSafe(req.dinner?.address?.country)} 
                                                                                svg 
                                                                                style={{ fontSize: '1em', marginLeft: 6, verticalAlign: 'middle' }} 
                                                                            />
                                                                        )}
                                                                    </div>
                                                                    <div style={{ color: '#22685b', fontSize: 15, marginTop: 2, textAlign: 'left' }}><b>Date:</b> {dateObj ? dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</div>
                                                                </div>
                                                            </div>

                                                            {hasReview ? (
                                                                <div>
                                                                    <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 8, color: '#15316b' }}>Your feedback:</div>
                                                                    <p style={{fontStyle: 'italic', color: '#555'}}>"{req.hostFeedback.text}"</p>
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 8, color: '#15316b' }}>What did you love most about the Shabbat dinner?</div>
                                                                    <textarea
                                                                        value={reviewInputs[req.id] || ''}
                                                                        onChange={e => handleReviewChange(req.id, e.target.value)}
                                                                        placeholder="A great vibe? Good food? Wonderful people? Share it with us"
                                                                        style={{ width: '100%', minHeight: 60, borderRadius: 10, border: '1px solid #e0e0e0', padding: 10, fontSize: 15, marginBottom: 10 }}
                                                                    />
                                                                    <button
                                                                        className="submit-feedback-btn"
                                                                                                                onClick={() => handleHostReviewSubmit(req.id, req.userId)}
                                                                        disabled={!reviewInputs[req.id]}
                                                                    >
                                                                        Submit Feedback
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                                {activePastTab === 'guest' && pastGuestRequests.length === 0 && (
                                                   <p style={{ textAlign: 'center', marginTop: 20, color: '#666' }}>You have no past dinners as a guest.</p>
                                                )}
                                                {activePastTab === 'host' && pastHostRequests.length === 0 && (
                                                    <p style={{ textAlign: 'center', marginTop: 20, color: '#666' }}>You have no past dinners as a host.</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {activeTab === 'dinner cards' && (
                                <div className="my-dinner-cards-section">
                                    <h2 className="pending-title">Edit / Delete Dinner Cards</h2>
                                    <div className="dinner-card-list grid">
                                        {myDinners.length === 0 ? (
                                            <p style={{ color: '#666'}}>You have no open dinners...</p>
                                        ) : (
                                            myDinners.map(dinner => (
                                                <div key={dinner.id} style={{ position: 'relative', marginBottom: 24 }}>
                                                    <HostCardProfile
                                                        dinner={dinner}
                                                        onCardClick={() => navigate(`/hostDinner?id=${dinner.id}`)}
                                                    />
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: 16,
                                                        right: 16,
                                                        display: 'flex',
                                                        gap: 8,
                                                        zIndex: 2
                                                    }}>
                                                        <button
                                                            title="Edit"
                                                            style={{
                                                                background: 'lightblue',
                                                                border: 'none',
                                                                borderRadius: '50%',
                                                                width: 36,
                                                                height: 36,
                                                                color: '#fff',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                                fontSize: 18
                                                            }}
                                                            onClick={() => navigate(`/hostDinnerDetails?id=${dinner.id}`)}
                                                        >
                                                            <FiEdit2
                                                                color = "white"
                                                                style={{cursor: "pointer"}}
                                                                onClick = {() => navigate ('/HostDinnerDetails?id=$(dinner.id)')}
                                                            />
                                                        </button>
                                                        <button
                                                            title="Delete"
                                                            style={{
                                                                background: '#e57373',
                                                                border: 'none',
                                                                borderRadius: '50%',
                                                                width: 36,
                                                                height: 36,
                                                                color: '#fff',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                                fontSize: 18
                                                            }}
                                                            onClick={() => setDinnerToDelete(dinner)}
                                                        >
                                                            <FiTrash2 color = "white" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                    {dinnerToDelete && (
                                        <div className="modal" style={{
                                            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                                            background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
                                        }}>
                                            <div style={{ background: '#fff', padding: 32, borderRadius: 16, minWidth: 320 }}>
                                                <h3>Are you sure you'd like to delete this dinner card?</h3>
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                                                    <button onClick={async () => {
                                                        await deleteDoc(doc(db, 'dinners', dinnerToDelete.id));
                                                        setMyDinners(myDinners.filter(d => d.id !== dinnerToDelete.id));
                                                        setDinnerToDelete(null);
                                                    }} style={{ background: '#e57373', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 22px', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}>
                                                        Delete
                                                    </button>
                                                    <button onClick={() => setDinnerToDelete(null)} style={{ background: '#eee', color: '#333', border: 'none', borderRadius: 8, padding: '8px 22px', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}>
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </main>
                    </div>
                </div>
            </div>
            {showProfileImgModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    background: 'rgba(0,0,0,0.55)',
                    zIndex: 99999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }} onClick={() => setShowProfileImgModal(false)}>
                    <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
                        <button onClick={() => setShowProfileImgModal(false)} style={{
                            position: 'absolute',
                            top: -18,
                            right: -18,
                            background: '#fff',
                            border: 'none',
                            borderRadius: '50%',
                            width: 36,
                            height: 36,
                            fontSize: 24,
                            color: '#16316a',
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.13)'
                        }}>×</button>
                        <img src={profileImg || '/blankprofile.png'} alt="Profile Large" style={{
                            width: 340,
                            height: 340,
                            objectFit: 'cover',
                            borderRadius: '50%',
                            boxShadow: '0 4px 32px rgba(0,0,0,0.18)'
                        }} />
                    </div>
                </div>
            )}
            {showPreview && (
                <PreviewProfileModal
                    open={showPreview}
                    onClose={() => setShowPreview(false)}
                    profile={{
                        uid: currentUser?.uid,
                        name: profileName,
                        avatar: profileImg,
                        bio: shortBioValue,
                        languages: languagesValue,
                        interests: interestsValue,
                        gallery: photos,
                        city: location?.city,
                        country: location?.country,
                        shabbotAnswers: shabbotAnswers
                    }}
                />
            )}
            {showShabbotChat && (
                <OnboardingChatbot
                    onClose={() => setShowShabbotChat(false)}
                    startedFromProfile={true}
                />
            )}
            {/* Preview modal for Past Dinners */}
            <PreviewProfileModal
                open={!!hostPreviewProfile}
                onClose={() => setHostPreviewProfile(null)}
                profile={hostPreviewProfile ? {
                    uid: hostPreviewProfile.uid || hostPreviewProfile.id,
                    avatar: hostPreviewProfile.profileImg,
                    name: hostPreviewProfile.name,
                    city: hostPreviewProfile.location?.city,
                    country: hostPreviewProfile.location?.country,
                    bio: hostPreviewProfile.about,
                    languages: hostPreviewProfile.languages,
                    interests: hostPreviewProfile.interests,
                    gallery: hostPreviewProfile.gallery,
                    shabbotAnswers: hostPreviewProfile.shabbotAnswers
                } : {}}
            />
        </>
    );
}

function ProfileSidebar({ location, setIsEditingLocation, activeTab, setActiveTab, isEditMode, setIsEditMode, profileImg, onProfileImgClick, profileImgInputRef, onProfileImgChange, profileName, setProfileName, handleFieldSave, isUploading }) {
    const [isEditingProfileName, setIsEditingProfileName] = React.useState(false);
    const [draftProfileName, setDraftProfileName] = React.useState(profileName);
    React.useEffect(() => { setDraftProfileName(profileName); }, [profileName]);
    return (
        <div className="profile-sidebar">
            <div className="profile-header">
                <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {isUploading ? (
                        <div className="profile-header-avatar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0' }}>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Uploading...</span>
                            </div>
                        </div>
                    ) : (
                        <img
                            src={profileImg || '/blankprofile.png'}
                            alt="Profile"
                            className="profile-header-avatar"
                            style={{ cursor: 'pointer', opacity: isEditMode ? 0.8 : 1, transition: 'opacity 0.2s' }}
                            onClick={onProfileImgClick}
                        />
                    )}
                    <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        ref={profileImgInputRef}
                        onChange={onProfileImgChange}
                    />
                    {isEditMode && <div className="profile-img-edit-hint">Click to change</div>}
                </div>
                <div className="profile-header-info" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <div
                        className="profile-header-name"
                        style={{ cursor: 'pointer', fontWeight: 700, fontSize: 24, marginBottom: 2 }}
                        onClick={() => setActiveTab('details')}
                        onDoubleClick={() => setIsEditingProfileName(true)}
                    >
                        {isEditingProfileName ? (
                            <input
                                type="text"
                                value={draftProfileName}
                                autoFocus
                                onChange={e => setDraftProfileName(e.target.value)}
                                onBlur={() => {
                                    setIsEditingProfileName(false);
                                    if (draftProfileName !== profileName) {
                                        setProfileName(draftProfileName);
                                        handleFieldSave('name', draftProfileName);
                                    }
                                }}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        setIsEditingProfileName(false);
                                        if (draftProfileName !== profileName) {
                                            setProfileName(draftProfileName);
                                            handleFieldSave('name', draftProfileName);
                                        }
                                    }
                                }}
                                style={{
                                    fontSize: 25,
                                    fontWeight: 700,
                                    color: '#16316a',
                                    fontFamily: "'Georgia', 'Times New Roman', serif",
                                    letterSpacing: '0.2px',
                                    marginBottom: 2,
                                    height: '32px',
                                    lineHeight: '32px',
                                    border: 'none',
                                    outline: 'none',
                                    background: 'none',
                                    padding: 0,
                                    width: '100%',
                                    boxSizing: 'border-box'
                                }}
                            />
                        ) : (
                            profileName || "Profile Name"
                        )}
                    </div>
                    <div className="profile-header-location" onClick={() => isEditMode && setIsEditingLocation(true)} style={{ cursor: isEditMode ? 'pointer' : 'default', fontSize: 16 }}>
                        {location && location.country
                            ? <>{location.city ? location.city + ', ' : ''}{location.country} {location.code && (<ReactCountryFlag countryCode={location.code} svg style={{ width: 14, height: 11, marginLeft: 6, verticalAlign: 'middle' }} />)}</>
                            : isEditMode ? "Add your location +" : "No location set"}
                    </div>
                </div>
            </div>
            <div
                className="profile-edit-profile"
                onClick={() => setIsEditMode(!isEditMode)}
                style={{ cursor: 'pointer' }}
            >
                {isEditMode ? 'Save Profile' : 'Edit Profile'}
            </div>
            <hr className="profile-divider" />
            <div className="profile-dinners-title">My Dinners</div>
            <div className="profile-dinners-list">
                <div className={`profile-dinner-link${activeTab === 'pending' ? ' profile-dinner-link-active' : ''}`} onClick={() => setActiveTab('pending')}>
                    Pending
                </div>
                <div className={`profile-dinner-link${activeTab === 'accepted' ? ' profile-dinner-link-active' : ''}`} onClick={() => setActiveTab('accepted')}>
                    Accepted
                </div>
                <div className={`profile-dinner-link${activeTab === 'past' ? ' profile-dinner-link-active' : ''}`} onClick={() => setActiveTab('past')}>
                    Past
                </div>
                <div className={`profile-dinner-link${activeTab === 'dinner cards' ? ' profile-dinner-link-active' : ''}`} onClick={() => setActiveTab('dinner cards')}> Edit / Delete Cards
                </div>
            </div>
        </div>
    );
}

function ProfileSection({ title, value, editable, defaultValue, onChange, alwaysBlack, renderEdit, renderValue }) {
    const [isEditing, setIsEditing] = useState(false);
    const [draftValue, setDraftValue] = useState(value);

    const isDefault = defaultValue !== undefined && value === defaultValue;

    const handleEditClick = () => {
        setDraftValue(isDefault ? '' : value);
        setIsEditing(true);
    };

    const handleSave = () => {
        setIsEditing(false);
        if (onChange) onChange(draftValue);
    };

    const handleCancel = () => {
        setDraftValue(value);
        setIsEditing(false);
    };

    // ברירת מחדל להצגה/עריכה
    const defaultRenderEdit = ({ value, onChange }) => (
        <textarea
            value={value}
            onChange={e => onChange(e.target.value)}
            autoFocus
            style={{ width: "100%", minHeight: "40px", fontFamily: 'inherit', fontSize: '15px', borderRadius: '8px', border: '1px solid #e0e0e0', padding: '8px' }}
        />
    );
    const defaultRenderValue = value => value;

    return (
        <div className="profile-section">
            <div className="profile-section-header">
                {title}
                {editable && !isEditing && (
                    <span className="profile-edit-icon" onClick={handleEditClick}>
                        <FiEdit2 size={18} />
                    </span>
                )}
            </div>
            <div
                className={`profile-section-value${!alwaysBlack && (isDefault || (isEditing && draftValue === defaultValue)) ? ' profile-section-default' : ''}`}
                onClick={editable && !isEditing ? handleEditClick : undefined}
                style={editable && !isEditing ? { cursor: 'pointer' } : {}}
            >
                {isEditing ? (
                    <div>
                        {(renderEdit || defaultRenderEdit)({ value: draftValue, onChange: setDraftValue })}
                        <div style={{ marginTop: 8 }}>
                            <button onClick={handleSave} style={{ marginRight: 8 }}>Save</button>
                            <button onClick={handleCancel}>Cancel</button>
                        </div>
                    </div>
                ) : (
                    (renderValue || defaultRenderValue)(value)
                )}
            </div>
        </div>
    );
}